'use client';

import { Member } from '@/app/(intakeFormV2)/intake-v2/types';
import { ActionButton } from '@/components/ui/ActionButton';
import { BackToTop } from '@/components/ui/BackToTop';
import { useCallback, useState } from 'react';
import { useIntakeWizardContext } from '../../contexts/WizardProvider';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { MemberDetailsForm } from '../common/MemberDetailsForm';

const isValidDateOfBirth = (dob: string): boolean => {
  if (!dob) return false;
  const match = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const [, month, day, year] = match;
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (
    date.getMonth() !== parseInt(month) - 1 ||
    date.getDate() !== parseInt(day)
  )
    return false;
  const today = new Date();
  const minDate = new Date(today.getFullYear() - 120, 0, 1);
  return date <= today && date >= minDate;
};

const toISODate = (dob: string): string => {
  const match = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return dob;
  const [, month, day, year] = match;
  return `${year}-${month}-${day}`;
};

const toDisplayDate = (dob: string): string => {
  if (!dob) return '';
  const match = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dob;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

export const QuoteMembersStep = () => {
  const { state, dispatch } = useIntakeWizardContext();
  const { goToStep } = useWizardNavigation();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize members from wizard state or with an empty member
  // Convert ISO dates (YYYY-MM-DD) back to display format (MM/DD/YYYY)
  const [members, setMembers] = useState<Member[]>(() => {
    if (state.formData.members && state.formData.members.length > 0) {
      return state.formData.members.map(m => ({
        ...m,
        date_of_birth: toDisplayDate(m.date_of_birth),
      }));
    }
    return [
      {
        id: crypto.randomUUID(),
        first_name: '',
        last_name: '',
        date_of_birth: '',
        isPrimary: true,
        plan: 'Signature',
        contact_id: crypto.randomUUID(),
      },
    ];
  });

  const handleMembersChange = useCallback((newMembers: Member[]) => {
    setMembers(newMembers);
    // Clear errors for fields that may have changed
    setErrors(prev => {
      const next = { ...prev };
      // Remove member-specific errors that may be stale
      Object.keys(next).forEach(key => {
        if (key.startsWith('member_')) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (members.length === 0) {
      newErrors.members = 'At least one member is required';
    }

    members.forEach((member, index) => {
      if (!member.first_name.trim()) {
        newErrors[`member_${index}_first_name`] = 'First name is required';
      }
      if (!member.last_name.trim()) {
        newErrors[`member_${index}_last_name`] = 'Last name is required';
      }
      if (!member.date_of_birth) {
        newErrors[`member_${index}_date_of_birth`] =
          'Date of birth is required';
      } else if (!isValidDateOfBirth(member.date_of_birth)) {
        newErrors[`member_${index}_date_of_birth`] =
          'Please enter a valid date of birth';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [members]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const membersForStorage = members.map(m => ({
        ...m,
        date_of_birth: toISODate(m.date_of_birth),
      }));

      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          members: membersForStorage,
          additional_members: membersForStorage.length > 1,
          number_of_members: membersForStorage.length,
        },
      });

      goToStep('quote-edit');
    },
    [members, validateForm, dispatch, goToStep]
  );

  const handleQuoteEdit = useCallback(() => {
    goToStep('quote-edit');
  }, [goToStep]);

  return (
    <div className='mx-auto w-full max-w-[768px] px-4 py-8'>
      <div className='flex flex-col gap-[40px]'>
        <div className='flex flex-col items-center gap-[24px] text-center xl:gap-[40px]'>
          <h1 className='font-serif text-[27px] leading-[115%] font-light text-[#292722] xl:text-[36px] xl:leading-[130%]'>
            Enter the details of each member you want to sign up.
          </h1>
          <p className='font-sans text-[18px] leading-[130%] font-normal text-[#292722] xl:text-[21px]'>
            Birth dates are used to personalize your quote.
          </p>
        </div>

        {/* Member line items */}
        <div className='space-y-0'>
          <MemberDetailsForm
            value={members}
            onChange={handleMembersChange}
            errors={errors}
          />
        </div>

        {/* Action buttons */}
        <div className='flex gap-[40px]'>
          <ActionButton
            label='Back'
            variant='primary'
            type='button'
            onClick={handleQuoteEdit}
            className='grow-1'
          />
          <ActionButton
            label='Continue'
            variant='secondary'
            type='button'
            onClick={handleSubmit}
            className='grow-1'
          />
        </div>

        <BackToTop />
      </div>
    </div>
  );
};
